import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography, Chip, Paper, Button, Divider, Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Alert, FormControlLabel, Checkbox, Switch, Grid, IconButton, Tooltip, Card } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LinkIcon from '@mui/icons-material/Link';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableQuestionItem from './components/SortableQuestionItem';
import { fetchEventById, removeParticipant as removeParticipantAction, updateParticipantAttendance as updateParticipantAttendanceAction, fetchParticipantSummary } from '../../controllers/clubEventsController';
import { fetchAllParticipationCancellationRequests } from '../../controllers/participationCancellationController';
import { ParticipationCancellationStatus } from '../../pages/events/participationCancellationConstants';
import { fetchLiveSurveysByEventId, createLiveSurvey, updateLiveSurvey, deleteLiveSurvey, fetchLiveSurveyById, addQuestionToSurvey, updateQuestionInSurvey, deleteQuestionFromSurvey, reorderSurveyQuestions, setSurveyActiveStatus, setSurveyCurrentQuestion, moveToNextQuestion, moveToPreviousQuestion, fetchQuestionReport } from '../../controllers/liveSurveyController';
import EditClubEventDialog from './EditClubEventDialog';
import { formatTurkeyLocalDateTime } from '../../utils/turkeyLocalDateTime';
import AddEventParticipantDialog from './AddEventParticipantDialog';
import EventMap from './components/EventMap';
import EntityGalleryPanel from '../../components/EntityGalleryPanel';
import { GALLERY_ENTITY_TYPE } from '../../constants/galleryEntityTypes';
import ParticipantSummary from './components/ParticipantSummary';
import ParticipantsTable from './components/ParticipantsTable';
import QRCodeDialog from './components/dialogs/QRCodeDialog';
import CapacityWarningDialog from './components/dialogs/CapacityWarningDialog';
import QuestionReportDialog from './components/dialogs/QuestionReportDialog';
import BackButton from '../../components/BackButton';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';

const DEFAULT_EVENT_INCOME_ARP_CODE = '603.003.116';

const ClubEventDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [summary, setSummary] = useState(null);
  const [capacityWarningOpen, setCapacityWarningOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { participantId, newStatus }
  // Live Survey states
  const [liveSurveys, setLiveSurveys] = useState([]);
  const [liveSurveysLoading, setLiveSurveysLoading] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [liveSurveyExpanded, setLiveSurveyExpanded] = useState(false);
    const [expandedSurveyId, setExpandedSurveyId] = useState(null);
    const [surveyDetailLoadingId, setSurveyDetailLoadingId] = useState(null);
  const [createSurveyDialogOpen, setCreateSurveyDialogOpen] = useState(false);
  const [editSurveyDialogOpen, setEditSurveyDialogOpen] = useState(false);
  const [deleteSurveyDialogOpen, setDeleteSurveyDialogOpen] = useState(false);
    const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [addQuestionDialogOpen, setAddQuestionDialogOpen] = useState(false);
  const [editQuestionDialogOpen, setEditQuestionDialogOpen] = useState(false);
  const [deleteQuestionDialogOpen, setDeleteQuestionDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [surveyForm, setSurveyForm] = useState({ title: '', description: '', questions: [] });
  const [questionForm, setQuestionForm] = useState({ questionText: '', questionType: 0, options: [''], isRequired: false, order: null });
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [pendingCancellationCount, setPendingCancellationCount] = useState(0);

  // Compute filteredParticipants BEFORE any early returns to keep hook order stable
  const filteredParticipants = useMemo(() => {
    const list = Array.isArray(item?.participants) ? item.participants : [];
    const q = search.trim().toLowerCase();
    const f = statusFilter === '' ? null : Number(statusFilter);
    const filtered = list.filter((p) => {
      const okSearch = !q || (p.fullName?.toLowerCase().includes(q)) || (p.phoneNumber?.toLowerCase().includes(q));
      const okStatus = f === null ? true : ((p.attendanceStatus ?? p.AttendanceStatus) === f);
      return okSearch && okStatus;
    });
    
    // Eğer "Sırada Bekliyor" filtresi seçilmişse, sıra numarasına göre sırala
    if (statusFilter === '4') {
      return filtered.sort((a, b) => {
        const queueA = a.queueNumber ?? a.QueueNumber ?? Infinity;
        const queueB = b.queueNumber ?? b.QueueNumber ?? Infinity;
        return queueA - queueB;
      });
    }
    
    return filtered;
  }, [item?.participants, search, statusFilter]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [data, sum] = await Promise.all([
          fetchEventById(id),
          fetchParticipantSummary(id).catch(() => null),
        ]);
        if (!active) return;
        setItem(data);
        setSummary(sum);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Etkinlik alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  // Fetch Live Surveys
  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      setLiveSurveysLoading(true);
      try {
        const surveys = await fetchLiveSurveysByEventId(id);
        if (!active) return;
        setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      } catch (e) {
        if (!active) return;
        setLiveSurveys([]);
      } finally {
        if (active) setLiveSurveysLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    fetchAllParticipationCancellationRequests({
      eventId: id,
      status: ParticipationCancellationStatus.Pending,
      pageNumber: 1,
      pageSize: 1,
    })
      .then(({ meta }) => {
        if (active) setPendingCancellationCount(meta?.totalCount ?? 0);
      })
      .catch(() => {
        if (active) setPendingCancellationCount(0);
      });
    return () => { active = false; };
  }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  if (!item) return null;

  const getParticipantTypeLabel = (typeValue) => {
    const v = typeof typeValue === 'number' ? typeValue : undefined;
    if (v === 0) return 'Üye';
    if (v === 1) return 'Yakın';
    return String(typeValue ?? '');
  };

  const getAttendanceLabel = (status) => {
    const v = typeof status === 'number' ? status : undefined;
    switch (v) {
      case 0: return 'Beklemede';
      case 1: return 'Katıldı';
      case 2: return 'Gelmedi';
      case 3: return 'İptal';
      case 4: return 'Sırada Bekliyor';
      default: return String(status ?? '');
    }
  };

  const getEventTypeLabel = (eventType) => {
    switch (eventType) {
      case 0: return 'Gezi';
      case 1: return 'Toplantı';
      case 2: return 'Sergi';
      case 3: return 'Konser';
      case 4: return 'Atölye';
      case 5: return 'Seminer';
      case 6: return 'Balo';
      case 7: return 'Yemek';
      case 8: return "Ladies' Night";
      default: return String(eventType ?? '');
    }
  };

    const formatDate = (dateString) => formatTurkeyLocalDateTime(dateString);

  // Live Survey Handlers
  const handleCreateSurvey = async () => {
    if (!item?.id || !surveyForm.title.trim()) return;
    try {
      const payload = {
        EventId: item.id,
        Title: surveyForm.title.trim(),
        Description: surveyForm.description.trim() || null,
        Questions: surveyForm.questions.map((q, idx) => ({
          QuestionText: q.questionText.trim(),
          QuestionType: Number(q.questionType),
          Options: q.questionType === 0 ? (q.options || []).filter(opt => opt.trim()) : null,
          Order: q.order !== undefined ? q.order : idx,
          IsRequired: q.isRequired || false,
        })),
      };
      await createLiveSurvey(payload);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      setCreateSurveyDialogOpen(false);
      setSurveyForm({ title: '', description: '', questions: [] });
    } catch (e) {
      alert(e?.message || 'Anket oluşturulamadı');
    }
  };

  const handleUpdateSurvey = async () => {
    if (!selectedSurvey?.id || !surveyForm.title.trim()) return;
    try {
      await updateLiveSurvey({
        Id: selectedSurvey.id,
        Title: surveyForm.title.trim(),
        Description: surveyForm.description.trim() || null,
      });
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
            // Güncellenmiş anket detayını tekrar fetch edip selectedSurvey state'ini güncelle
            const updatedSurveyDetail = await fetchLiveSurveyById(selectedSurvey.id);
            setSelectedSurvey(updatedSurveyDetail);
      setSurveyForm({ title: '', description: '', questions: [] });
            setEditSurveyDialogOpen(false);
    } catch (e) {
      alert(e?.message || 'Anket güncellenemedi');
    }
  };

  const handleDeleteSurvey = async () => {
        if (!surveyToDelete?.id) return;
    try {
            await deleteLiveSurvey(surveyToDelete.id);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      setDeleteSurveyDialogOpen(false);
            setSurveyToDelete(null);
            // Eğer silinen anket seçili anket ise, seçimi temizle
            if (selectedSurvey?.id === surveyToDelete.id) {
      setSelectedSurvey(null);
            }
    } catch (e) {
      alert(e?.message || 'Anket silinemedi');
    }
  };

  const handleAddQuestion = async () => {
    if (!selectedSurvey?.id || !questionForm.questionText.trim()) return;
    if (questionForm.questionType === 0 && (!questionForm.options || questionForm.options.filter(opt => opt.trim()).length < 2)) {
      alert('Çoktan seçmeli sorular için en az 2 seçenek gerekli');
      return;
    }
    try {
      const payload = {
        QuestionText: questionForm.questionText.trim(),
        QuestionType: Number(questionForm.questionType),
        Options: questionForm.questionType === 0 ? questionForm.options.filter(opt => opt.trim()) : null,
        Order: questionForm.order,
        IsRequired: questionForm.isRequired || false,
      };
      await addQuestionToSurvey(selectedSurvey.id, payload);
      const survey = await fetchLiveSurveyById(selectedSurvey.id);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      setAddQuestionDialogOpen(false);
      setQuestionForm({ questionText: '', questionType: 0, options: [''], isRequired: false, order: null });
    } catch (e) {
      alert(e?.message || 'Soru eklenemedi');
    }
  };

  const handleUpdateQuestion = async () => {
    if (!selectedSurvey?.id || !selectedQuestion?.id || !questionForm.questionText.trim()) return;
    if (questionForm.questionType === 0 && (!questionForm.options || questionForm.options.filter(opt => opt.trim()).length < 2)) {
      alert('Çoktan seçmeli sorular için en az 2 seçenek gerekli');
      return;
    }
    try {
      const payload = {
        QuestionId: selectedQuestion.id,
        QuestionText: questionForm.questionText.trim(),
        QuestionType: Number(questionForm.questionType),
        Options: questionForm.questionType === 0 ? questionForm.options.filter(opt => opt.trim()) : null,
        Order: questionForm.order !== null ? questionForm.order : selectedQuestion.order,
        IsRequired: questionForm.isRequired || false,
      };
      await updateQuestionInSurvey(selectedSurvey.id, payload);
      const survey = await fetchLiveSurveyById(selectedSurvey.id);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      setEditQuestionDialogOpen(false);
      setSelectedQuestion(null);
      setQuestionForm({ questionText: '', questionType: 0, options: [''], isRequired: false, order: null });
    } catch (e) {
      alert(e?.message || 'Soru güncellenemedi');
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedSurvey?.id || !selectedQuestion?.id) return;
    try {
      await deleteQuestionFromSurvey(selectedSurvey.id, selectedQuestion.id);
      const survey = await fetchLiveSurveyById(selectedSurvey.id);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      setDeleteQuestionDialogOpen(false);
      setSelectedQuestion(null);
    } catch (e) {
      alert(e?.message || 'Soru silinemedi');
    }
  };

  const handleReorderQuestions = async (surveyId, questionIds) => {
    try {
      await reorderSurveyQuestions(surveyId, questionIds);
      const survey = await fetchLiveSurveyById(surveyId);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
    } catch (e) {
      alert(e?.message || 'Sorular sıralanamadı');
    }
  };

  const handleSetSurveyActive = async (surveyId, isActive) => {
    try {
      await setSurveyActiveStatus(surveyId, isActive);
      const survey = await fetchLiveSurveyById(surveyId);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
      
      // Eğer anket başlatıldıysa yeni sekmede kontrol sayfasını aç
      if (isActive) {
        const controlUrl = `/live-survey/${surveyId}`;
        window.open(controlUrl, '_blank');
      }
    } catch (e) {
      alert(e?.message || 'Anket durumu güncellenemedi');
    }
  };

  const handleSetCurrentQuestion = async (surveyId, questionIndex) => {
    try {
      await setSurveyCurrentQuestion(surveyId, questionIndex);
      const survey = await fetchLiveSurveyById(surveyId);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
    } catch (e) {
      alert(e?.message || 'Aktif soru güncellenemedi');
    }
  };

  const handleNextQuestion = async (surveyId) => {
    try {
      await moveToNextQuestion(surveyId);
      const survey = await fetchLiveSurveyById(surveyId);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
    } catch (e) {
      alert(e?.message || 'Sonraki soruya geçilemedi');
    }
  };

  const handlePreviousQuestion = async (surveyId) => {
    try {
      await moveToPreviousQuestion(surveyId);
      const survey = await fetchLiveSurveyById(surveyId);
      setSelectedSurvey(survey);
      const surveys = await fetchLiveSurveysByEventId(item.id);
      setLiveSurveys(Array.isArray(surveys) ? surveys : []);
    } catch (e) {
      alert(e?.message || 'Önceki soruya geçilemedi');
    }
  };

  const handleViewReport = async (surveyId, questionId) => {
    try {
      const report = await fetchQuestionReport(surveyId, questionId);
      setReportData(report);
      setReportDialogOpen(true);
    } catch (e) {
      alert(e?.message || 'Rapor alınamadı');
    }
  };

  // EventMap hoisted above

  // (moved filteredParticipants above)

  return (
    <Stack spacing={2}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton />
      </Stack>
      {pendingCancellationCount > 0 && (
        <Alert severity="warning" sx={{ fontSize: '13px' }}>
          Bu etkinlik için {pendingCancellationCount} bekleyen katılım iptal talebi var.{' '}
          <RouterLink
            to={`/events/cancellation-requests?eventId=${id}&status=0`}
            style={{ color: '#0E4D94', fontWeight: 600 }}
          >
            Talepleri görüntüle
          </RouterLink>
        </Alert>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>{item.title}</Typography>
      <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setEditOpen(true)}
                        startIcon={<EditIcon sx={{ width: 16 }} />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#E7EDF4',
                            boxShadow: 'none',
                            fontSize: 13,
                            borderRadius: '5px',
                            color: '#0E4D94',
                            '&:hover': {
                                backgroundColor: '#CFDBEA',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Düzenle
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setQrOpen(true)}
                        sx={{
                            textTransform: 'none',
                            fontSize: 13,
                            borderRadius: '5px',
                        }}
                    >
                        QR Kodu Görüntüle
                    </Button>
      </Stack>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 3fr' }, gap: 2 }}>
                {/* Sol Kart - Etkinlik Bilgileri (1/4) */}
                <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
                    <Stack spacing={2}>
                        {(item.coverImageUrl || item.CoverImageUrl) && (
                          <Box
                            component="img"
                            src={resolveGalleryAssetUrl(item.coverImageUrl || item.CoverImageUrl)}
                            alt={item.title}
                            sx={{
                              width: '100%',
                              maxHeight: 220,
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #e6e6e6',
                            }}
                          />
                        )}
                        {/* Tarih */}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <ScheduleIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Başlangıç Tarihi</Typography>
                                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                    {formatDate(item.startDate)}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <ScheduleIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Bitiş Tarihi</Typography>
                                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                    {formatDate(item.endDate)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Durum */}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            {item.isActive ? (
                                <CheckCircleIcon sx={{ fontSize: '20px', color: '#17C653', mt: 0.5, flexShrink: 0 }} />
                            ) : (
                                <RadioButtonUncheckedIcon sx={{ fontSize: '20px', color: '#F8285A', mt: 0.5, flexShrink: 0 }} />
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Durum</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    <Chip
                                        label={item.isActive ? 'Aktif' : 'Pasif'}
                                        size="small"
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            backgroundColor: item.isActive ? '#DFFFEA' : '#FFEEF3',
                                            color: item.isActive ? '#17C653' : '#F8285A',
                                            height: 24,
                                        }}
                                    />
                                    {item.isCompleted && (
                                        <Chip
                                            label="Tamamlandı"
                                            size="small"
                                            sx={{
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                backgroundColor: '#DFFFEA',
                                                color: '#17C653',
                                                height: 24,
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </Box>

                        {/* Tür */}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <CategoryIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Tür</Typography>
                                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                    {getEventTypeLabel(item.eventType)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Kapasite */}
        {typeof item.registeredCount === 'number' && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <PeopleIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Kayıtlı</Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                        {item.registeredCount} {typeof item.capacity === 'number' ? `/ ${item.capacity}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
        )}

                        {/* Ücret */}
        {typeof item.price === 'number' && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <AttachMoneyIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Ücret</Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                        {item.price} TL
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Logo gelir cari kodu */}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <AccountBalanceIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                                        Logo gelir cari kodu
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                        {item.logoCariCode || `Varsayılan (${DEFAULT_EVENT_INCOME_ARP_CODE})`}
                                    </Typography>
                                    {!item.logoCariCode && (
                                        <Typography variant="body2" sx={{ fontSize: '12px', color: '#a3a3a3', mt: 0.25 }}>
                                            Etkinlik borç fişinde varsayılan gelir hesabı kullanılır.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                        {/* Misafir Bilgileri */}
        {item.allowGuests && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <EventAvailableIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Misafir</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        <Chip
                                            label="Misafir Getirilebilir"
                                            size="small"
                                            sx={{ backgroundColor: '#E7EDF4', color: '#0E4D94', fontWeight: 600, fontSize: '12px', height: 24 }}
                                        />
                                        {typeof item.guestCapacity === 'number' && item.guestCapacity > 0 && (
                                            <Chip
                                                label={`Kapasite: ${item.guestCapacity}/üye`}
                                                size="small"
                                                sx={{ fontSize: '12px', fontWeight: 500, borderColor: '#0E4D94', color: '#0E4D94', height: 24 }}
                                                variant="outlined"
                                            />
        )}
      </Stack>
                                </Box>
                </Box>
              )}

                        {/* Online Toplantı Linki */}
      {item?.onlineMeetingLink && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <LinkIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Online Toplantı Linki</Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
                                            size="small"
              startIcon={<LaunchIcon />}
              href={item.onlineMeetingLink}
              target="_blank"
              rel="noopener noreferrer"
                                            sx={{
                                                alignSelf: 'flex-start',
                                                textTransform: 'none',
                                                fontSize: 13,
                                                borderRadius: '5px',
                                            }}
            >
              Toplantıya Katıl
            </Button>
                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', wordBreak: 'break-all' }}>
              {item.onlineMeetingLink}
            </Typography>
          </Stack>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Card>

                {/* Sağ Kart - Açıklama ve Konum (3/4) */}
                <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
                    <Stack spacing={3}>
                        {/* Açıklama */}
                        {item?.description && (
                            <Box>
                                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 1.5 }}>
                                    Açıklama
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '14px', color: '#444444' }}>
                                    {item.description}
                                </Typography>
                            </Box>
      )}

                        {/* Konum */}
                        {item?.location && (
                            <>
                                {item?.description && <Divider sx={{ borderColor: '#e6e6e6' }} />}
                                <Box>
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                        <LocationOnIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                                        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                            Konum
                                        </Typography>
                                    </Box>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}>
                                        <Stack spacing={1.5} sx={{ minWidth: 220 }}>
                                            {item.location.city && (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Şehir</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                                        {item.location.city}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {item.location.address && (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Adres</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                                        {item.location.address}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {item.location.venueName && (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Mekan</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                                        {item.location.venueName}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {item.location.country && (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Ülke</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                                        {item.location.country}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {typeof item.location.latitude === 'number' && typeof item.location.longitude === 'number' && (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Koordinat</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                                                        {item.location.latitude}, {item.location.longitude}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                        {typeof item.location.latitude === 'number' && typeof item.location.longitude === 'number' && (
                                            <Box sx={{ flex: 1, minWidth: 260 }}>
                                                <EventMap latitude={item.location.latitude} longitude={item.location.longitude} />
                                            </Box>
                                        )}
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Stack>
                </Card>
            </Box>
      <EntityGalleryPanel
        relatedEntityType={GALLERY_ENTITY_TYPE.ClubEvent}
        relatedEntityId={id}
        initialGalleryId={item.galleryId || item.GalleryId}
        embeddedGallery={item.gallery || item.Gallery}
      />
      {/* Live Survey Section */}
            <Card sx={{ px: 2.5, py: 1, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
                <Accordion expanded={liveSurveyExpanded} onChange={(e, exp) => setLiveSurveyExpanded(exp)} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#0E4D94' }} />}
                        sx={{
                            px: 0,
                            '& .MuiAccordionSummary-content': {
                                my: 0,
                            },
                        }}
                    >
                        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                            Canlı Anketler
                        </Typography>
          </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 2 }}>
            {liveSurveysLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : liveSurveys.length === 0 ? (
                            <Stack spacing={2} alignItems="center">
                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                                    Henüz canlı anket oluşturulmamış.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateSurveyDialogOpen(true)}
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: '#E7EDF4',
                                        boxShadow: 'none',
                                        fontSize: 13,
                                        borderRadius: '5px',
                                        color: '#0E4D94',
                                        '&:hover': {
                                            backgroundColor: '#CFDBEA',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                  Anket Oluştur
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    Etkinlik için {liveSurveys.length} anket bulundu
                  </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setCreateSurveyDialogOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            backgroundColor: '#E7EDF4',
                                            boxShadow: 'none',
                                            fontSize: 13,
                                            borderRadius: '5px',
                                            color: '#0E4D94',
                                            '&:hover': {
                                                backgroundColor: '#CFDBEA',
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                    Yeni Anket
                  </Button>
                </Stack>
                {liveSurveys.map((survey) => (
                                    <Paper key={survey.id} variant="outlined" sx={{ p: 0, border: '1px solid #e6e6e6', boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
                                        <Accordion
                                            expanded={expandedSurveyId === survey.id}
                                            onChange={async (e, exp) => {
                                                setExpandedSurveyId(exp ? survey.id : null);
                                                if (!exp) return;
                                                try {
                                                    setSurveyDetailLoadingId(survey.id);
                                const surveyDetail = await fetchLiveSurveyById(survey.id);
                                setSelectedSurvey(surveyDetail);
                                setSurveyForm({
                                  title: surveyDetail.title || '',
                                  description: surveyDetail.description || '',
                                  questions: surveyDetail.questions || [],
                                });
                                                } catch (err) {
                                                    alert(err?.message || 'Anket detayı alınamadı');
                                                } finally {
                                                    setSurveyDetailLoadingId(null);
                                                }
                                            }}
                                            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                          >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#0E4D94' }} />}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    '& .MuiAccordionSummary-content': { my: 0 },
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }} gap={2}>
                                                    <Stack>
                                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                                                {survey.title}
                                                            </Typography>
                                                            {survey.isActive && (
                                                                <Chip
                                                                    label="Aktif"
                                                                    size="small"
                                                                    sx={{ backgroundColor: '#DFFFEA', color: '#17C653', fontWeight: 600, fontSize: '12px' }}
                                                                />
                                                            )}
                                                        </Stack>
                                                        {survey.description && (
                                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mt: 0.5 }}>
                                                                {survey.description}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mt: 0.5 }}>
                                                            {survey.questionCount || 0} soru
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center" onClick={(evt) => evt.stopPropagation()}>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                                                    // Eğer selectedSurvey zaten varsa ve id'si eşleşiyorsa, tekrar API çağrısı yapma
                                                                    if (selectedSurvey?.id === survey.id) {
                                                                        setSurveyForm({
                                                                            title: selectedSurvey.title || '',
                                                                            description: selectedSurvey.description || '',
                                                                            questions: selectedSurvey.questions || [],
                                                                        });
                                                                        setEditSurveyDialogOpen(true);
                                                                        return;
                                                                    }
                                                                    // Eğer selectedSurvey yoksa veya id'si eşleşmiyorsa, API çağrısı yap
                                                                    try {
                                                                        setSurveyDetailLoadingId(survey.id);
                                  const surveyDetail = await fetchLiveSurveyById(survey.id);
                                  setSelectedSurvey(surveyDetail);
                                  setSurveyForm({
                                    title: surveyDetail.title || '',
                                    description: surveyDetail.description || '',
                                    questions: surveyDetail.questions || [],
                                  });
                                  setEditSurveyDialogOpen(true);
                                                                    } catch (err) {
                                                                        alert(err?.message || 'Anket detayı alınamadı');
                                                                    } finally {
                                                                        setSurveyDetailLoadingId(null);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    backgroundColor: '#E7EDF4',
                                                                    borderRadius: '6px',
                                                                    width: 32,
                                                                    height: 32,
                                                                    '&:hover': { backgroundColor: '#CFDBEA' },
                                                                    '& svg': { fontSize: '16px', color: '#0E4D94' },
                              }}
                            >
                                                                <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                                                                aria-label="Sil"
                              onClick={() => {
                                                                    setSurveyToDelete(survey);
                                setDeleteSurveyDialogOpen(true);
                              }}
                                                                sx={{
                                                                    backgroundColor: '#FFEEF3',
                                                                    borderRadius: '6px',
                                                                    width: 32,
                                                                    height: 32,
                                                                    '&:hover': { backgroundColor: '#FFD6E0' },
                                                                    '& svg': { fontSize: '16px', color: '#F8285A' },
                                                                }}
                            >
                                                                <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                                                {surveyDetailLoadingId === survey.id ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                                        <CircularProgress size={24} />
                                                    </Box>
                                                ) : selectedSurvey?.id !== survey.id ? (
                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', py: 1 }}>
                                                        Detay yüklenemedi.
                                                    </Typography>
                                                ) : (
                        <Box>
                                                        <Divider sx={{ mb: 2 }} />
                          <Stack spacing={2}>
                                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                                                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                                Sorular ({selectedSurvey.questions?.length || 0})
                              </Typography>
                                                                <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
                                <Button
                                  size="small"
                                  variant={selectedSurvey.isActive ? 'contained' : 'outlined'}
                                  startIcon={selectedSurvey.isActive ? <StopIcon /> : <PlayArrowIcon />}
                                  onClick={() => handleSetSurveyActive(selectedSurvey.id, !selectedSurvey.isActive)}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            fontSize: '14px',
                                                                            borderRadius: '5px',
                                                                            px: 2,
                                                                            ...(selectedSurvey.isActive
                                                                                ? {
                                                                                    backgroundColor: '#F8285A',
                                                                                    color: '#ffffff',
                                                                                    '&:hover': {
                                                                                        backgroundColor: '#d91e47',
                                                                                    },
                                                                                }
                                                                                : {
                                                                                    borderColor: '#0E4D94',
                                                                                    color: '#0E4D94',
                                                                                    '&:hover': {
                                                                                        borderColor: '#0a3d7a',
                                                                                        backgroundColor: '#E7EDF4',
                                                                                    },
                                                                                }),
                                                                        }}
                                >
                                  {selectedSurvey.isActive ? 'Durdur' : 'Başlat'}
                                </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<AddIcon />}
                                                                        onClick={() => {
                                  const maxOrder = selectedSurvey.questions && selectedSurvey.questions.length > 0
                                    ? Math.max(...selectedSurvey.questions.map(q => q.order || 0))
                                    : -1;
                                  setQuestionForm({
                                    questionText: '',
                                    questionType: 0,
                                    options: [''],
                                    isRequired: false,
                                    order: maxOrder + 1,
                                  });
                                  setSelectedQuestion(null);
                                  setAddQuestionDialogOpen(true);
                                                                        }}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            fontSize: '14px',
                                                                            borderRadius: '5px',
                                                                            px: 2,
                                                                            borderColor: '#0E4D94',
                                                                            color: '#0E4D94',
                                                                            '&:hover': {
                                                                                borderColor: '#0a3d7a',
                                                                                backgroundColor: '#E7EDF4',
                                                                            },
                                                                        }}
                                                                    >
                                  Soru Ekle
                                </Button>
                              </Stack>
                            </Stack>

                            {selectedSurvey.isActive && (
                                                                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#E7EDF4', border: '1px solid #CFDBEA', borderRadius: '8px' }}>
                                                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#444444' }}>
                                    Aktif Soru: {selectedSurvey.currentQuestionIndex >= 0 && selectedSurvey.questions
                                      ? `${selectedSurvey.currentQuestionIndex + 1}. ${selectedSurvey.questions.find(q => q.order === selectedSurvey.currentQuestionIndex)?.questionText || 'Yok'}`
                                      : 'Sorular gösterilmiyor'}
                                  </Typography>
                                                                        <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                                    <Tooltip title="Önceki Soru">
                                      <IconButton
                                        size="small"
                                        disabled={selectedSurvey.currentQuestionIndex <= 0}
                                        onClick={() => handlePreviousQuestion(selectedSurvey.id)}
                                                                                    sx={{ backgroundColor: '#ffffff', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                      >
                                        <ArrowBackIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Sonraki Soru">
                                      <IconButton
                                        size="small"
                                        disabled={selectedSurvey.currentQuestionIndex >= (selectedSurvey.questions?.length || 0) - 1}
                                        onClick={() => handleNextQuestion(selectedSurvey.id)}
                                                                                    sx={{ backgroundColor: '#ffffff', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                      >
                                        <ArrowForwardIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                                                                    const questionIndex = selectedSurvey.currentQuestionIndex >= 0 ? selectedSurvey.currentQuestionIndex : 0;
                                        handleSetCurrentQuestion(selectedSurvey.id, questionIndex);
                                      }}
                                                                                sx={{ textTransform: 'none', fontSize: 13, borderRadius: '5px' }}
                                    >
                                      İlk Soruyu Göster
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleSetCurrentQuestion(selectedSurvey.id, -1)}
                                                                                sx={{ textTransform: 'none', fontSize: 13, borderRadius: '5px' }}
                                    >
                                      Soruları Gizle
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Paper>
                            )}

                            {selectedSurvey.questions && selectedSurvey.questions.length > 0 ? (
                                                                <Box sx={{ mt: 1 }}>
                              <DndContext
                                collisionDetection={closestCenter}
                                onDragEnd={async (event) => {
                                  const { active, over } = event;
                                  if (!over || active.id === over.id) return;
                                  const questions = [...selectedSurvey.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
                                  const oldIndex = questions.findIndex((q) => q.id === active.id);
                                  const newIndex = questions.findIndex((q) => q.id === over.id);
                                  if (oldIndex === -1 || newIndex === -1) return;
                                  const reordered = arrayMove(questions, oldIndex, newIndex);
                                  const questionIds = reordered.map((q) => q.id);
                                  await handleReorderQuestions(selectedSurvey.id, questionIds);
                                }}
                              >
                                <SortableContext items={selectedSurvey.questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                                  {selectedSurvey.questions
                                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                                    .map((question, idx) => (
                                      <SortableQuestionItem
                                        key={question.id}
                                        question={question}
                                        index={idx}
                                        isCurrent={selectedSurvey.isActive && selectedSurvey.currentQuestionIndex === question.order}
                                        onEdit={(q) => {
                                          setSelectedQuestion(q);
                                          setQuestionForm({
                                            questionText: q.questionText || '',
                                            questionType: q.questionType || 0,
                                                                                                options: q.questionType === 0 && q.options && q.options.length > 0 ? [...q.options] : [''],
                                            isRequired: q.isRequired || false,
                                            order: q.order,
                                          });
                                          setEditQuestionDialogOpen(true);
                                        }}
                                        onDelete={(q) => {
                                          setSelectedQuestion(q);
                                          setDeleteQuestionDialogOpen(true);
                                        }}
                                        onViewReport={(questionId) => handleViewReport(selectedSurvey.id, questionId)}
                                      />
                                    ))}
                                </SortableContext>
                              </DndContext>
                                                                </Box>
                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        mt: 2,
                                                                        p: 3,
                                                                        border: '1px dashed #e6e6e6',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: '#fafafa',
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                                Henüz soru eklenmemiş. Soru eklemek için yukarıdaki butonu kullanın.
                              </Typography>
                                                                </Box>
                            )}
                          </Stack>
                        </Box>
                      )}
                                            </AccordionDetails>
                                        </Accordion>
                  </Paper>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
            </Card>
      <ParticipantSummary summary={summary} />
      {Array.isArray(item.participants) && (
        <ParticipantsTable
          participants={filteredParticipants}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          eventId={item.id}
          allowGuests={item.allowGuests === true}
          onAttendanceStatusChange={async (participant, nextVal) => {
            const currentStatus = typeof (participant.attendanceStatus ?? participant.AttendanceStatus) === 'number' ? (participant.attendanceStatus ?? participant.AttendanceStatus) : null;
            
            // Eğer Sırada Bekliyor (4) durumundan Beklemede (0) durumuna geçiliyorsa uyarı göster
            if (currentStatus === 4 && nextVal === 0) {
              setPendingStatusChange({ participantId: participant.id, newStatus: nextVal });
              setCapacityWarningOpen(true);
              return;
            }
            
            // Normal durum değişikliği
            try {
              await updateParticipantAttendanceAction({ EventId: item.id, ParticipantId: participant.id, AttendanceStatus: nextVal });
              const [data, sum] = await Promise.all([
                fetchEventById(item.id),
                fetchParticipantSummary(item.id).catch(() => null),
              ]);
              setItem(data);
              setSummary(sum);
                        } catch (err) { }
          }}
          onRemoveParticipant={async (participant) => {
            try {
              await removeParticipantAction({ EventId: item.id, ParticipantId: participant.id });
              const data = await fetchEventById(item.id);
              setItem(data);
                        } catch (e) { }
          }}
          onGuestUpdated={async () => {
            try {
              const [data, sum] = await Promise.all([
                fetchEventById(item.id),
                fetchParticipantSummary(item.id).catch(() => null),
              ]);
              setItem(data);
              setSummary(sum);
                        } catch (e) { }
          }}
          getParticipantTypeLabel={getParticipantTypeLabel}
                    onAddClick={() => setAddOpen(true)}
        />
      )}
      <QRCodeDialog open={qrOpen} onClose={() => setQrOpen(false)} event={item} />
      <AddEventParticipantDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        eventId={item.id}
        event={item}
        onAdded={async () => {
          try {
            const data = await fetchEventById(id);
            setItem(data);
                    } catch { }
        }}
      />
      <EditClubEventDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        eventData={item}
        onSaved={async () => {
          setEditOpen(false);
          try {
            const data = await fetchEventById(id);
            setItem(data);
                    } catch { }
        }}
      />
      <CapacityWarningDialog
        open={capacityWarningOpen}
        onClose={() => { setCapacityWarningOpen(false); setPendingStatusChange(null); }}
        onConfirm={async () => {
          if (!pendingStatusChange) return;
          try {
            await updateParticipantAttendanceAction({ 
              EventId: item.id, 
              ParticipantId: pendingStatusChange.participantId, 
              AttendanceStatus: pendingStatusChange.newStatus 
            });
            const [data, sum] = await Promise.all([
              fetchEventById(item.id),
              fetchParticipantSummary(item.id).catch(() => null),
            ]);
            setItem(data);
            setSummary(sum);
            setCapacityWarningOpen(false);
            setPendingStatusChange(null);
          } catch (err) {
            // Error handling
          }
        }}
        event={item}
        summary={summary}
      />

      {/* Create Live Survey Dialog */}
      <Dialog open={createSurveyDialogOpen} onClose={() => setCreateSurveyDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Yeni Canlı Anket
                    <IconButton
                        onClick={() => setCreateSurveyDialogOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Başlık
                            </Typography>
            <TextField
              fullWidth
              required
              value={surveyForm.title}
              onChange={(e) => setSurveyForm((f) => ({ ...f, title: e.target.value }))}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Açıklama (Opsiyonel)
                            </Typography>
            <TextField
              fullWidth
              multiline
                                minRows={3}
              value={surveyForm.description}
              onChange={(e) => setSurveyForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        px: 0,
                                        py: 0,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
            />
                        </Box>
                        <Alert severity="info" sx={{ fontSize: '13px' }}>
                            Anket oluşturulduktan sonra sorular ekleyebilirsiniz.
                        </Alert>
          </Stack>
        </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setCreateSurveyDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateSurvey}
                        disabled={!surveyForm.title.trim()}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#0a3d7a',
                            },
                            '&:disabled': {
                                backgroundColor: '#e6e6e6',
                                color: '#a3a3a3',
                            },
                        }}
                    >
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Live Survey Dialog */}
      <Dialog open={editSurveyDialogOpen} onClose={() => setEditSurveyDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Anketi Düzenle
                    <IconButton
                        onClick={() => setEditSurveyDialogOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Başlık
                            </Typography>
            <TextField
              fullWidth
              required
              value={surveyForm.title}
              onChange={(e) => setSurveyForm((f) => ({ ...f, title: e.target.value }))}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Açıklama (Opsiyonel)
                            </Typography>
            <TextField
              fullWidth
              multiline
                                minRows={3}
              value={surveyForm.description}
              onChange={(e) => setSurveyForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        px: 0,
                                        py: 0,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
            />
                        </Box>
          </Stack>
        </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setEditSurveyDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateSurvey}
                        disabled={!surveyForm.title.trim()}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#0a3d7a',
                            },
                            '&:disabled': {
                                backgroundColor: '#e6e6e6',
                                color: '#a3a3a3',
                            },
                        }}
                    >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Live Survey Dialog */}
            <Dialog open={deleteSurveyDialogOpen} onClose={() => {
                setDeleteSurveyDialogOpen(false);
                setSurveyToDelete(null);
            }}>
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Anketi Sil
                    <IconButton
                        onClick={() => {
                            setDeleteSurveyDialogOpen(false);
                            setSurveyToDelete(null);
                        }}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            Bu anketi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => {
                            setDeleteSurveyDialogOpen(false);
                            setSurveyToDelete(null);
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteSurvey}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#F8285A',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#d91e47',
                            },
                        }}
                    >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Question Dialog */}
      <Dialog open={addQuestionDialogOpen} onClose={() => setAddQuestionDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Soru Ekle
                    <IconButton
                        onClick={() => setAddQuestionDialogOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Soru Metni
                            </Typography>
            <TextField
              fullWidth
              required
              multiline
                                minRows={2}
              value={questionForm.questionText}
              onChange={(e) => setQuestionForm((f) => ({ ...f, questionText: e.target.value }))}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        p: 1.5,
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        px: 0,
                                        py: 0,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Soru Tipi
                            </Typography>
            <TextField
              select
              fullWidth
              value={questionForm.questionType}
              onChange={(e) => {
                const newType = Number(e.target.value);
                setQuestionForm((f) => ({
                  ...f,
                  questionType: newType,
                  options: newType === 0 ? [''] : null,
                }));
              }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
            >
              <MenuItem value={0}>Çoktan Seçmeli</MenuItem>
              <MenuItem value={1}>Değerlendirme (1-5)</MenuItem>
              <MenuItem value={2}>Sayı</MenuItem>
            </TextField>
                        </Box>
            {questionForm.questionType === 0 && (
              <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Seçenekler
                </Typography>
                                <Box
                                    sx={{
                                    }}
                                >
                                    <Stack spacing={1.5}>
                  {questionForm.options.map((opt, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e6e6e6',
                                                    borderRadius: '6px',
                                                    '&:hover': {
                                                        borderColor: '#0E4D94',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#E7EDF4',
                                                        borderRadius: '6px',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        color: '#0E4D94',
                                                    }}
                                                >
                                                    {idx + 1}
                                                </Box>
                      <TextField
                        fullWidth
                                                    size="small"
                                                    placeholder={`Seçenek ${idx + 1} metnini girin`}
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...questionForm.options];
                          newOptions[idx] = e.target.value;
                          setQuestionForm((f) => ({ ...f, options: newOptions }));
                        }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '5px',
                                                            backgroundColor: '#ffffff',
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#0E4D94',
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            py: 1,
                                                            px: 1.5,
                                                            fontSize: '14px',
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#e6e6e6',
                                                        },
                                                    }}
                      />
                      {questionForm.options.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newOptions = questionForm.options.filter((_, i) => i !== idx);
                            setQuestionForm((f) => ({ ...f, options: newOptions.length > 0 ? newOptions : [''] }));
                          }}
                                                        sx={{
                                                            backgroundColor: '#FFEEF3',
                                                            color: '#F8285A',
                                                            width: 32,
                                                            height: 32,
                                                            flexShrink: 0,
                                                            '&:hover': {
                                                                backgroundColor: '#FFD6E0',
                                                            },
                                                        }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                                            </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setQuestionForm((f) => ({ ...f, options: [...f.options, ''] }));
                    }}
                                            sx={{
                                                borderColor: '#e6e6e6',
                                                borderStyle: 'dashed',
                                                color: '#444444',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                py: 1.2,
                                                backgroundColor: '#ffffff',
                                                '&:hover': {
                                                    borderColor: '#0E4D94',
                                                    backgroundColor: '#E7EDF4',
                                                    borderStyle: 'solid',
                                                },
                                            }}
                  >
                    Seçenek Ekle
                  </Button>
                </Stack>
                                </Box>
              </Box>
            )}
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Sıra (Opsiyonel)
                            </Typography>
            <TextField
              fullWidth
              type="number"
              value={questionForm.order !== null ? questionForm.order : ''}
              onChange={(e) => {
                const val = e.target.value;
                setQuestionForm((f) => ({ ...f, order: val === '' ? null : Number(val) }));
              }}
                                placeholder=""
              helperText="Boş bırakılırsa en sona eklenir"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '12px',
                                        color: '#666666',
                                        mt: 0.5,
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Zorunlu soru
                            </Typography>
            <FormControlLabel
              control={
                                    <Switch
                  checked={questionForm.isRequired}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, isRequired: e.target.checked }))}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#0E4D94',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#0E4D94',
                                            },
                                        }}
                />
              }
                                label={questionForm.isRequired ? 'Evet' : 'Hayır'}
                                sx={{
                                    ml: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '13px',
                                        ml: 1,
                                    },
                                }}
            />
                        </Box>
          </Stack>
        </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setAddQuestionDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '14px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        İptal
                    </Button>
          <Button
            variant="contained"
            onClick={handleAddQuestion}
            disabled={!questionForm.questionText.trim() || (questionForm.questionType === 0 && (!questionForm.options || questionForm.options.filter(opt => opt.trim()).length < 2))}
                        sx={{
                            textTransform: 'none',
                            fontSize: '14px',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#0a3d7a',
                            },
                            '&:disabled': {
                                backgroundColor: '#e6e6e6',
                                color: '#a3a3a3',
                            },
                        }}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editQuestionDialogOpen} onClose={() => setEditQuestionDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Soruyu Düzenle
                    <IconButton
                        onClick={() => setEditQuestionDialogOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Soru Metni
                            </Typography>
            <TextField
              fullWidth
              required
              multiline
                                minRows={2}
              value={questionForm.questionText}
              onChange={(e) => setQuestionForm((f) => ({ ...f, questionText: e.target.value }))}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        p: 1.5,
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        px: 0,
                                        py: 0,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Soru Tipi
                            </Typography>
            <TextField
              select
              fullWidth
              value={questionForm.questionType}
              onChange={(e) => {
                const newType = Number(e.target.value);
                setQuestionForm((f) => ({
                  ...f,
                  questionType: newType,
                  options: newType === 0 ? (f.options && f.options.length > 0 ? f.options : ['']) : null,
                }));
              }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
            >
              <MenuItem value={0}>Çoktan Seçmeli</MenuItem>
              <MenuItem value={1}>Değerlendirme (1-5)</MenuItem>
              <MenuItem value={2}>Sayı</MenuItem>
            </TextField>
                        </Box>
            {questionForm.questionType === 0 && (
              <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Seçenekler
                </Typography>
                                <Box
                                    sx={{
                                    }}
                                >
                                    <Stack spacing={1.5}>
                  {questionForm.options.map((opt, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e6e6e6',
                                                    borderRadius: '6px',
                                                    '&:hover': {
                                                        borderColor: '#0E4D94',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#E7EDF4',
                                                        borderRadius: '6px',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        color: '#0E4D94',
                                                    }}
                                                >
                                                    {idx + 1}
                                                </Box>
                      <TextField
                        fullWidth
                                                    size="small"
                                                    placeholder={`Seçenek ${idx + 1} metnini girin`}
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...questionForm.options];
                          newOptions[idx] = e.target.value;
                          setQuestionForm((f) => ({ ...f, options: newOptions }));
                        }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '5px',
                                                            backgroundColor: '#ffffff',
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#0E4D94',
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            py: 1,
                                                            px: 1.5,
                                                            fontSize: '14px',
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#e6e6e6',
                                                        },
                                                    }}
                      />
                      {questionForm.options.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newOptions = questionForm.options.filter((_, i) => i !== idx);
                            setQuestionForm((f) => ({ ...f, options: newOptions.length > 0 ? newOptions : [''] }));
                          }}
                                                        sx={{
                                                            backgroundColor: '#FFEEF3',
                                                            color: '#F8285A',
                                                            width: 32,
                                                            height: 32,
                                                            flexShrink: 0,
                                                            '&:hover': {
                                                                backgroundColor: '#FFD6E0',
                                                            },
                                                        }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                                            </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setQuestionForm((f) => ({ ...f, options: [...f.options, ''] }));
                    }}
                                            sx={{
                                                borderColor: '#e6e6e6',
                                                borderStyle: 'dashed',
                                                color: '#444444',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                py: 1.2,
                                                backgroundColor: '#ffffff',
                                                '&:hover': {
                                                    borderColor: '#0E4D94',
                                                    backgroundColor: '#E7EDF4',
                                                    borderStyle: 'solid',
                                                },
                                            }}
                  >
                    Seçenek Ekle
                  </Button>
                </Stack>
                                </Box>
              </Box>
            )}
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Sıra (Opsiyonel)
                            </Typography>
            <TextField
              fullWidth
              type="number"
              value={questionForm.order !== null ? questionForm.order : ''}
              onChange={(e) => {
                const val = e.target.value;
                setQuestionForm((f) => ({ ...f, order: val === '' ? null : Number(val) }));
              }}
                                placeholder=""
                                helperText="Boş bırakılırsa en sona eklenir"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '12px',
                                        color: '#666666',
                                        mt: 0.5,
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Zorunlu soru
                            </Typography>
            <FormControlLabel
              control={
                                    <Switch
                  checked={questionForm.isRequired}
                  onChange={(e) => setQuestionForm((f) => ({ ...f, isRequired: e.target.checked }))}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#0E4D94',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#0E4D94',
                                            },
                                        }}
                />
              }
                                label={questionForm.isRequired ? 'Evet' : 'Hayır'}
                                sx={{
                                    ml: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '13px',
                                        ml: 1,
                                    },
                                }}
            />
                        </Box>
          </Stack>
        </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setEditQuestionDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '14px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        İptal
                    </Button>
          <Button
            variant="contained"
            onClick={handleUpdateQuestion}
            disabled={!questionForm.questionText.trim() || (questionForm.questionType === 0 && (!questionForm.options || questionForm.options.filter(opt => opt.trim()).length < 2))}
                        sx={{
                            textTransform: 'none',
                            fontSize: '14px',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#0a3d7a',
                            },
                            '&:disabled': {
                                backgroundColor: '#e6e6e6',
                                color: '#a3a3a3',
                            },
                        }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Question Dialog */}
      <Dialog open={deleteQuestionDialogOpen} onClose={() => setDeleteQuestionDialogOpen(false)}>
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Soruyu Sil
                    <IconButton
                        onClick={() => setDeleteQuestionDialogOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            Bu soruyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setDeleteQuestionDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteQuestion}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#F8285A',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#d91e47',
                            },
                        }}
                    >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <QuestionReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        reportData={reportData}
        selectedSurvey={selectedSurvey}
      />
    </Stack>
  );
};

export default ClubEventDetail;


